import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarCheck, Users, BellRing, MessageCircle, Star, CalendarX2, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "../Reveal";

const MODULES = [
  {
    icon: CalendarCheck,
    tag: "Smart Booking",
    title: "Rezerwacje online, kalendarz i przypomnienia",
    desc: "Klient wybiera usługę, dostępny termin i samodzielnie rezerwuje wizytę online. System uwzględnia rzeczywisty grafik firmy - godziny pracy, urlopy, przerwy, dni wolne i zajęte terminy. Zespół może ręcznie dodawać wizyty, planować kolejne terminy i blokować wybrane godziny. Automatyczne potwierdzenia i przypomnienia SMS lub e-mail pomagają ograniczyć pomyłki i nieobecności.",
    benefit: "Jeden kalendarz. Realna dostępność. Mniej telefonów. Mniej pomyłek w grafiku.",
  },
  {
    icon: Users,
    tag: "Smart Waitlist",
    title: "Zapełnianie zwolnionych terminów",
    desc: "Gdy ktoś odwoła wizytę, system proponuje zwolniony termin klientom czekającym na wcześniejszy termin. Mniej pustych okienek i lepsze wykorzystanie czasu pracy.",
  },
  {
    icon: BellRing,
    tag: "Smart Recall",
    title: "Przypomnienia o powrocie",
    desc: "System sam kontaktuje się z klientem po ustalonym czasie - o wizycie kontrolnej, kolejnym zabiegu, przeglądzie, konsultacji czy odnowieniu usługi. Pomaga sprowadzać stałych klientów w odpowiednim momencie.",
  },
  {
    icon: MessageCircle,
    tag: "Smart Follow-up",
    title: "Kontakt po usłudze",
    desc: "Po zakończonej wizycie system może automatycznie wysłać podziękowanie, zalecenia po zabiegu, przydatne materiały oraz zaproszenie do umówienia kolejnej wizyty - także po wybranym czasie.",
  },
  {
    icon: Star,
    tag: "Smart Reviews",
    title: "Automatyczne zbieranie opinii",
    desc: "Po usłudze klient otrzymuje wiadomość z prośbą o opinię i bezpośrednim linkiem do jej wystawienia. Prosty sposób na więcej opinii - bez pytania każdego klienta osobno.",
  },
  {
    icon: CalendarX2,
    tag: "Smart No-show",
    title: "Mniej nieodbytych wizyt",
    desc: "Potwierdzenia i przypomnienia przed wizytą pomagają ograniczyć nieobecności klientów. Klient może w prosty sposób potwierdzić, przełożyć lub odwołać termin. Jeśli wizyta zostanie odwołana, wolny termin może zostać wykorzystany w Smart Waitlist. Efekt: mniej pustych okienek i lepiej wykorzystany grafik.",
  },
];

export const ClientFlow = () => (
  <section className="bg-[#050505] text-white py-24 md:py-32 px-6" data-testid="client-flow-section">
    <div className="max-w-6xl mx-auto">
      <div className="max-w-3xl mb-14 md:mb-16">
        <Reveal><Overline dark>Veliora Client Flow</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tightest leading-[1.05] font-semibold">
            Inteligentna <span className="font-serif-italic font-normal" style={{ color: "var(--gold)" }}>ścieżka klienta</span> — od pierwszego kontaktu po jego powrót.
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-4 text-sm uppercase tracking-[0.2em]" style={{ color: "var(--gold)" }}>
            Rezerwacje • Przypomnienia • Kontakt z klientem • Opinie
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-lg text-white/70 leading-relaxed">
            Modułowy system automatyzacji dla firm pracujących z klientami, wizytami, rezerwacjami i usługami cyklicznymi. Moduły działają osobno lub razem, jako jedna spójna ścieżka klienta.
          </p>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-4 text-base text-white/55 leading-relaxed">
            Sprawdza się m.in. w gabinetach stomatologicznych, klinikach, u fizjoterapeutów, w salonach beauty i fryzjerskich, u barberów, konsultantów, trenerów, specjalistów oraz w firmach usługowych pracujących na wizytach.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {MODULES.map((m, i) => (
          <Reveal key={m.tag} delay={(i % 3) * 0.08} className="h-full">
            <motion.div
              data-testid={`client-flow-module-${i}`}
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 300, damping: 22 }}
              className="group h-full bg-white/[0.03] border border-white/10 rounded-3xl p-8 flex flex-col transition-colors duration-500 hover:border-[var(--gold)]/40"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="h-[52px] w-[52px] rounded-2xl border border-white/15 flex items-center justify-center text-white transition-colors duration-500 group-hover:border-[var(--gold)] group-hover:text-[var(--gold)]">
                  <m.icon size={22} strokeWidth={1.5} />
                </div>
                <span className="text-xs font-mono text-white/25">0{i + 1}</span>
              </div>
              <p className="text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gold)" }}>{m.tag}</p>
              <h3 className="text-xl font-semibold tracking-tight mb-3 leading-snug">{m.title}</h3>
              <p className="text-[15px] text-white/70 leading-relaxed">{m.desc}</p>
              {m.benefit && (
                <p className="mt-5 pt-5 border-t border-white/10 text-[15px] font-serif-italic" style={{ color: "var(--gold)" }}>
                  {m.benefit}
                </p>
              )}
            </motion.div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-14 md:mt-16 rounded-3xl border border-white/10 bg-white/[0.02] px-8 py-10 md:px-14 md:py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h3 className="text-2xl sm:text-3xl tracking-tight font-semibold leading-tight max-w-2xl">
            Jedna ścieżka klienta. Mniej ręcznej pracy. Mniej pustych terminów. <span className="font-serif-italic font-normal" style={{ color: "var(--gold)" }}>Więcej powracających klientów.</span>
          </h3>
          <Link
            to="/contact"
            data-testid="client-flow-cta"
            className="group shrink-0 self-start md:self-auto inline-flex flex-col items-start md:items-end gap-2"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-6 py-3.5 transition-transform duration-300 group-hover:-translate-y-0.5">
              Sprawdź Client Flow dla swojej firmy
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </span>
            <span className="text-sm text-white/45 leading-relaxed">Dobierzemy moduły do sposobu pracy Twojej firmy.</span>
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
